import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api-client';
import { MAX_SUPERADMIN_AGE_SECONDS } from '../../lib/constants';
import { useCurrentUser } from './useAuth';

export function useElevatedAuth() {
  const navigate = useNavigate();
  const { data: userProfile } = useCurrentUser();

  const elevateAuth = useMutation({
    mutationFn: async () => {
      const response = await apiClient<{ token: string; message: string }>('/api/auth/elevate', {
        method: 'POST',
      });

      return response;
    },
    onSuccess: (_data) => {
      // The server sets a fresh JWT in HTTP-only cookies
      // No need to manually store tokens anymore

      // Navigate to superadmin panel
      navigate('/superadmin');
    },
  });

  const checkElevated = () => {
    if (!userProfile?.user) return false;

    // Check if user has superadmin role and JWT is fresh enough
    const isSuperAdmin = userProfile.user.roles.includes('superadmin');
    const isTokenFresh = userProfile.jwtAge <= MAX_SUPERADMIN_AGE_SECONDS;

    return isSuperAdmin && isTokenFresh;
  };

  const clearElevated = () => {
    // Do nothing - elevation clears automatically when token ages out
    // This function exists for backwards compatibility
  };

  return {
    elevate: elevateAuth.mutate,
    isElevating: elevateAuth.isPending,
    error: elevateAuth.error,
    isElevated: checkElevated(),
    clearElevated,
  };
}
